import { AzureFileSystem } from '@/components/file-manager/azure-blob-storage/AzureFileSystem'
import { AzureGateway } from '@/components/file-manager/azure-blob-storage/AzureGateway'
import { calculatePath } from '@/components/file-manager/azure-blob-storage/utils'
import { AxiosResponse } from 'axios'
import CustomFileSystemProvider from 'devextreme/file_management/custom_provider'
import FileSystemItem from 'devextreme/file_management/file_system_item'
import UploadInfo from 'devextreme/file_management/upload_info'

export class AzureCustomProvider {
  fileSystemProvider: CustomFileSystemProvider

  azure: AzureFileSystem

  gateway: AzureGateway

  rootPath: string

  createDirectoryAction: (rootFolderPath: string, name: string, conflictBerhavior: string) => Promise<AxiosResponse>

  constructor(
    endpointUrl: string,
    rootPath: string,
    createDirectoryAction: (rootFolderPath: string, name: string, conflictBerhavior: string) => Promise<AxiosResponse>,
    onRequestExecuted?: ({
      method,
      urlPath,
      queryString,
    }: {
      method: string
      urlPath: string
      queryString: string
    }) => void,
  ) {
    this.gateway = new AzureGateway(endpointUrl, onRequestExecuted)
    this.azure = new AzureFileSystem(this.gateway)
    this.rootPath = rootPath
    this.createDirectoryAction = createDirectoryAction
    const options = {
      getItems: this.getItems,
      createDirectory: this.createDirectory,
      renameItem: this.renameItem,
      deleteItem: this.deleteItem,
      copyItem: this.copyItem,
      moveItem: this.moveItem,
      uploadFileChunk: this.uploadFileChunk,
      downloadItems: this.downloadItems,
    }
    this.fileSystemProvider = new CustomFileSystemProvider(options)
  }

  getItems = (parentDirectory: FileSystemItem) => {
    const path = calculatePath(parentDirectory.path, this.rootPath)
    return this.azure.getItems(path)
  }

  createDirectory = async (parentDirectory: FileSystemItem, name: string): Promise<any> => {
    await this.createDirectoryAction(this.rootPath, '', '')
    const path = calculatePath(parentDirectory.path, this.rootPath)
    return this.azure.createDirectory(path, name)
  }

  renameItem = (item: FileSystemItem, name: string): Promise<any> => {
    const path = calculatePath(item.path, this.rootPath)
    return item.isDirectory ? this.azure.renameDirectory(path, name) : this.azure.renameFile(path, name)
  }

  deleteItem = (item: FileSystemItem): Promise<any> => {
    const path = calculatePath(item.path, this.rootPath)
    return item.isDirectory ? this.azure.deleteDirectory(path) : this.azure.deleteFile(path)
  }

  copyItem = (item: FileSystemItem, destinationDirectory: FileSystemItem): Promise<any> => {
    const path = calculatePath(item.path, this.rootPath)
    const destinationPath = calculatePath(
      destinationDirectory.path ? `${destinationDirectory.path}/${item.name}` : item.name,
      this.rootPath,
    )
    return item.isDirectory
      ? this.azure.copyDirectory(path, destinationPath)
      : this.azure.copyFile(path, destinationPath)
  }

  moveItem = (item: FileSystemItem, destinationDirectory: FileSystemItem): Promise<any> => {
    const path = calculatePath(item.path, this.rootPath)
    const destinationPath = calculatePath(
      destinationDirectory.path ? `${destinationDirectory.path}/${item.name}` : item.name,
      this.rootPath,
    )
    return item.isDirectory
      ? this.azure.moveDirectory(path, destinationPath)
      : this.azure.moveFile(path, destinationPath)
  }

  uploadFileChunk = async (
    fileData: File,
    uploadInfo: UploadInfo,
    destinationDirectory: FileSystemItem,
  ): Promise<any> => {
    let promise = null

    // Controllo ogni volta l'esistenza della cartalla principale
    try {
      promise = await this.createDirectoryAction(this.rootPath, '', '')
    } catch {
      // Non segnalo niente la creazione potrebbe essere già avvenuta
    }

    const filePath = calculatePath(
      destinationDirectory.path ? `${destinationDirectory.path}/${fileData.name}` : fileData.name,
      this.rootPath,
    )

    if (uploadInfo.chunkIndex === 0) {
      promise = this.gateway.getUploadAccessUrl(filePath).then((accessUrls: any) => {
        uploadInfo.customData.accessUrl = accessUrls.url1
      })
    } else {
      promise = Promise.resolve()
    }

    promise = promise.then(() =>
      this.gateway.putBlock(uploadInfo.customData.accessUrl, uploadInfo.chunkIndex, uploadInfo.chunkBlob),
    )

    if (uploadInfo.chunkIndex === uploadInfo.chunkCount - 1) {
      promise = promise.then(() => this.gateway.putBlockList(uploadInfo.customData.accessUrl, uploadInfo.chunkCount))
    }

    return promise
  }

  downloadItems = (items: FileSystemItem[]): void => {
    const path = calculatePath(items[0].path, this.rootPath)
    this.azure.downloadFile(path)
  }
}
